import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apiFetch, ApiError } from '@/api/client'
import { useThemeStore, type ThemePreferences } from '@/stores/theme'

export interface AuthUser {
  id: number
  username: string
  displayName: string | null
  role: string
  roles: string[]
  /** Chinese/display names aligned with roles[] */
  roleNames?: string[]
  permissions: string[]
  /** Chinese/display names aligned with permissions[] */
  permissionNames?: string[]
}

export interface MenuNode {
  id: number
  parentId: number
  name: string
  path: string | null
  icon: string | null
  permissionCode: string | null
  sortOrder: number
  type: string
  children?: MenuNode[]
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const menus = ref<MenuNode[]>([])
  const permissions = ref<string[]>([])
  const bootstrapped = ref(false)
  const loading = ref(false)

  const isAuthenticated = computed(() => Boolean(user.value))
  const isSuper = computed(() => permissions.value.includes('*'))

  function hasPermission(code: string): boolean {
    if (permissions.value.includes('*')) return true
    return permissions.value.includes(code)
  }

  function hasAnyPermission(codes: string[]): boolean {
    return codes.some((c) => hasPermission(c))
  }

  function applyAuthPayload(data: {
    user: AuthUser
    menus?: MenuNode[]
    permissions?: string[]
    preferences?: ThemePreferences | null
  }) {
    user.value = {
      ...data.user,
      roles: data.user.roles || [],
      permissions: data.user.permissions || data.permissions || [],
    }
    permissions.value = data.permissions || data.user.permissions || []
    menus.value = data.menus || []
    // Theme follows the account across devices; see hydrate() for the precedence
    // rules between the server value and what localStorage already painted.
    useThemeStore().hydrate(data.preferences)
  }

  async function fetchMe(): Promise<boolean> {
    try {
      const data = await apiFetch<{
        user: AuthUser
        menus: MenuNode[]
        permissions: string[]
        preferences?: ThemePreferences | null
      }>('/api/auth/me')
      applyAuthPayload(data)
      return true
    } catch (err) {
      user.value = null
      menus.value = []
      permissions.value = []
      if (err instanceof ApiError && err.status === 401) return false
      return false
    } finally {
      bootstrapped.value = true
    }
  }

  async function login(username: string, password: string, remember: boolean) {
    loading.value = true
    try {
      const data = await apiFetch<{ user: AuthUser }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password, remember }),
      })
      // login returns user with permissions; refresh menus via me
      user.value = {
        ...data.user,
        roles: data.user.roles || [],
        permissions: data.user.permissions || [],
      }
      permissions.value = data.user.permissions || []
      bootstrapped.value = true
      await fetchMe()
      return data.user
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await apiFetch<null>('/api/auth/logout', { method: 'POST' })
    } finally {
      user.value = null
      menus.value = []
      permissions.value = []
      // Stop pushing preferences upstream until the next account hydrates.
      useThemeStore().resetSync()
      try {
        const { useNoticeStore } = await import('@/stores/notice')
        useNoticeStore().reset()
      } catch {
        /* ignore */
      }
    }
  }

  return {
    user,
    menus,
    permissions,
    bootstrapped,
    loading,
    isAuthenticated,
    isSuper,
    hasPermission,
    hasAnyPermission,
    fetchMe,
    login,
    logout,
  }
})
